import React, { useEffect, useState } from "react";
import {
    Avatar,
    Typography,
    Paper,
    Grid,
    Chip,
    Tooltip,
    Box,
    Tab,
    Tabs,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import "./ProfileDashboard.css";

import { Card, CardContainer } from "../../../components/Card";
import { styled } from "@mui/styles";
import Posts from "../posts/Posts";
import { useTheme } from "@mui/material/styles";

import Audience from "../audience/Audience";
import Mentions from "../mentions/Mentions";
import Engagement from "../engagement/Engagement";
import { NoBackpackSharp } from "@mui/icons-material";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 10.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: "none",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    "&.Mui-selected": {
        color: theme.palette.secondary[500],
    },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.neutral[200]}`,
    "& .MuiTabs-indicator": {
        backgroundColor: theme.palette.secondary[500],
    },
}));

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const ProfileDashboard = ({ profileData, platform, showTabs = false }) => {
    // Add null check for profileData
    const { profile, work_platform, metrics } = profileData ?? {};
    // Add null checks for profile and metrics
    const safeProfile = profile ?? {};
    const safeMetrics = metrics ?? {};
    const isYouTube = platform === "youtube";

    const [value, setValue] = useState(0);

    const tabs = isYouTube
        ? [
              { id: 0, title: "Engagement" },
              { id: 1, title: "Audience" },
              { id: 2, title: "Content" },
          ]
        : [
              { id: 0, title: "Engagement" },
              { id: 1, title: "Audience" },
              { id: 2, title: "Content" },
              { id: 3, title: "Mentions" },
          ];

    const TabList = isYouTube
        ? [Engagement, Audience, Posts]
        : [Engagement, Audience, Posts, Mentions];

    const ActiveTab = TabList[value]; // Get active tab component
    const theme = useTheme();
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const [truncatedIntro, setTruncatedIntro] = useState("");
    const maxLength = 200;

    useEffect(() => {
        if (safeProfile?.introduction?.length > maxLength) {
            setTruncatedIntro(
                safeProfile.introduction.slice(0, maxLength) + "..."
            );
        } else {
            setTruncatedIntro(safeProfile.introduction || "");
        }
    }, [safeProfile?.introduction]);

    if (!profileData) {
        return (
            <Box className="profile-dashboard">
                <Typography variant="h6">Profile data unavailable</Typography>
            </Box>
        );
    }

    return (
        <Box className="profile-dashboard export-section">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "row",
                    height: "100%",
                }}
                className="profile-header"
            >
                <div style={{ width: "70%" }}>
                    <CardContainer>
                        <Card height="24vh">
                            <Grid container spacing={3} alignItems="center">
                                <Grid item>
                                    <Avatar
                                        alt={safeProfile.name || ""}
                                        src={safeProfile.image_url || ""}
                                        sx={{ width: 100, height: 100 }}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <Grid
                                        container
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Grid item>
                                            <Typography
                                                variant="highlightSemibold"
                                                component="h1"
                                            >
                                                {safeProfile.name || ""}{" "}
                                                {safeProfile.is_verified && (
                                                    <Tooltip title="Verified Account">
                                                        <VerifiedIcon
                                                            color="secondary"
                                                            className="verified-icon"
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            {safeProfile.url && (
                                                <a
                                                    href={safeProfile.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: theme.palette
                                                            .graph?.cyan,
                                                        textDecoration:
                                                            "underline",
                                                        cursor: "pointer",
                                                    }}
                                                    className="no-export" // Don't show in PDF
                                                >
                                                    View Profile
                                                </a>
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Typography variant="captionRegular">
                                        @{safeProfile.username || ""}
                                    </Typography>
                                    <br />
                                    <Typography variant="captionRegular">
                                        {truncatedIntro || ""}{" "}
                                    </Typography>

                                    {profileData?.summary?.location && (
                                        <Typography
                                            variant="captionRegular"
                                            className="location"
                                        >
                                            <LocationOnIcon fontSize="small" />
                                            {`${profileData.summary.location || ""}`}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Card>
                    </CardContainer>
                </div>
                <div
                    style={{
                        width: "30%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        marginLeft: "20px",
                    }}
                >
                    <CardContainer>
                        <Card>
                            <Typography
                                variant="captionRegular"
                                sx={{
                                    color: theme.palette.neutral?.[300],
                                }}
                            >
                                Total Posts{" "}
                            </Typography>
                            <br />
                            <Typography
                                variant="contentAccent"
                                sx={{
                                    color: theme.palette.neutral?.[500],
                                }}
                            >
                                {" "}
                                {(safeMetrics.contents || 0).toLocaleString()}
                            </Typography>
                        </Card>
                        <Card>
                            <Typography
                                variant="captionRegular"
                                sx={{
                                    color: theme.palette.neutral?.[300],
                                }}
                            >
                                {isYouTube ? "Subscribers" : "Total Followers"}
                            </Typography>{" "}
                            <Typography
                                variant="contentAccent"
                                sx={{
                                    color: theme.palette.neutral?.[500],
                                }}
                            >
                                {(safeMetrics.followers || 0).toLocaleString()}
                            </Typography>
                        </Card>
                    </CardContainer>
                    <CardContainer>
                        <Card>
                            <Typography
                                variant="captionRegular"
                                sx={{
                                    color: theme.palette.neutral?.[300],
                                }}
                            >
                                Total views
                            </Typography>{" "}
                            <Typography
                                variant="contentAccent"
                                sx={{
                                    color: theme.palette.neutral?.[500],
                                }}
                            >
                                {(safeMetrics.views || 0).toLocaleString()}
                            </Typography>
                        </Card>
                        <Card>
                            <Typography
                                variant="captionRegular"
                                sx={{
                                    color: theme.palette.neutral?.[300],
                                }}
                            >
                                Engagement
                            </Typography>{" "}
                            <Typography
                                variant="contentAccent"
                                sx={{
                                    color: theme.palette.neutral?.[500],
                                }}
                            >
                                {`${((safeMetrics.engagement_rate || 0) * 100).toFixed(2)}%`}
                            </Typography>
                        </Card>
                    </CardContainer>
                </div>
            </div>

            {/* Only show tabs if showTabs is true */}
            {showTabs && (
                <div className="tab-content">
                    <StyledTabs
                        value={value}
                        onChange={handleChange}
                        aria-label="BBS tabs"
                    >
                        {tabs.map((tab, index) => (
                            <StyledTab
                                key={tab.id}
                                label={tab.title}
                                {...a11yProps(index)}
                            />
                        ))}
                    </StyledTabs>

                    <ActiveTab data={profileData} platform={platform} />
                </div>
            )}

            {/* Only show these sections if showTabs is true and platform is not YouTube */}
            {showTabs && !isYouTube && (
                <Grid
                    container
                    spacing={3}
                    className="dashboard-sections export-section"
                >
                    {safeProfile.top_hashtags?.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <Paper elevation={3} className="dashboard-section">
                                <Typography
                                    variant="highlightSemibold"
                                    component="h2"
                                >
                                    Top Hashtags
                                </Typography>
                                <Box className="hashtags-container">
                                    {(safeProfile.top_hashtags || [])
                                        .slice(0, 10)
                                        .map((hashtag, index) => (
                                            <Tooltip
                                                key={index}
                                                title={`Value: ${(hashtag?.value || 0).toFixed(2)}`}
                                            >
                                                <Chip
                                                    label={`#${hashtag?.name || ""}`}
                                                    className="hashtag-chip"
                                                />
                                            </Tooltip>
                                        ))}
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                    {safeProfile.top_mentions?.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <Paper elevation={3} className="dashboard-section">
                                <Typography
                                    variant="highlightSemibold"
                                    component="h2"
                                >
                                    Top Mentions
                                </Typography>
                                <Box className="mentions-container">
                                    {(safeProfile.top_mentions || [])
                                        .slice(0, 10)
                                        .map((mention, index) => (
                                            <Tooltip
                                                key={index}
                                                title={`Value: ${(mention?.value || 0).toFixed(2)}`}
                                            >
                                                <Chip
                                                    label={`@${mention?.name || ""}`}
                                                    className="mention-chip"
                                                />
                                            </Tooltip>
                                        ))}
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
};

export default ProfileDashboard;
